/**
 * Modul D API Client - centralized helpers
 * Base: /api/modul-d/*
 */
import api from "@/lib/api";

// ─── Policy / Feature flags ─────────────────────────────────────────
export const getFeatureFlags = () => api.get("/modul-d/feature-flags").then(r => r.data);

// ─── Conversations ──────────────────────────────────────────────────
export const listConversations = (params = {}) =>
  api.get("/modul-d/conversations", { params }).then(r => r.data);

export const getConversation = (conversationId) =>
  api.get(`/modul-d/conversations/${conversationId}`).then(r => r.data);

export const sendMessage = (conversationId, text, metadata = {}) =>
  api.post(`/modul-d/conversations/${conversationId}/messages`, { text, metadata }).then(r => r.data);

// ─── Marketplace Inquiry ────────────────────────────────────────────
export const createInquiry = (listingId, message) =>
  api.post("/modul-d/inquiries", { listing_id: listingId, message }).then(r => r.data);

// ─── Pre-Sale (STX) ─────────────────────────────────────────────────
export const createPresale = (data) =>
  api.post("/modul-d/presale", data).then(r => r.data);

export const listSellerPresales = () =>
  api.get("/modul-d/presale/seller").then(r => r.data);

export const listBuyerPresales = () =>
  api.get("/modul-d/presale/buyer").then(r => r.data);

export const getPresale = (presaleId) =>
  api.get(`/modul-d/presale/${presaleId}`).then(r => r.data);

export const sellerDecide = (presaleId, body) =>
  api.post(`/modul-d/presale/${presaleId}/seller-decide`, body).then(r => r.data);

export const buyerCounterRespond = (presaleId, decision) =>
  api.post(`/modul-d/presale/${presaleId}/buyer-counter-respond`, { decision }).then(r => r.data);

export const sellerWithdraw = (presaleId) =>
  api.post(`/modul-d/presale/${presaleId}/seller-withdraw`).then(r => r.data);

// ─── Pre-Sale Text Messages (free-text replies, 4c.bug11) ────────────
export const listPresaleMessages = (presaleId) =>
  api.get(`/modul-d/presale/${presaleId}/messages`).then(r => r.data);

export const sendPresaleMessage = (presaleId, text) =>
  api.post(`/modul-d/presale/${presaleId}/message`, { text }).then(r => r.data);

// ─── Notifications (mark as read) ─────────────────────────────────────
export const markNotificationsRead = (scope = {}) =>
  api.post("/messages/notifications/mark-read", scope).then(r => {
    // Fire global event so the BottomNav badge refreshes immediately
    try { window.dispatchEvent(new CustomEvent("unread-refresh")); } catch { /* ssr-safe */ }
    return r.data;
  });

// ─── After-Sale Chat (Order-Lookup) ─────────────────────────────────
export const getConversationByOrder = (orderId) =>
  api.get(`/modul-d/conversations/by-order/${orderId}`).then(r => r.data);

// ─── KI Suggestions ─────────────────────────────────────────────────
export const getSuggestions = (conversationId, category) =>
  api.post("/modul-d/suggestions", { conversation_id: conversationId, category }).then(r => r.data);

// ─── Admin ──────────────────────────────────────────────────────────
export const adminGetPolicy = () => api.get("/modul-d/admin/policy").then(r => r.data);
export const adminUpdatePolicy = (area, config) => api.put(`/modul-d/admin/policy/${area}`, config).then(r => r.data);
export const adminTestPolicy = (text, area, sender_role = "BUYER") =>
  api.post("/modul-d/admin/policy/test", { text, area, sender_role }).then(r => r.data);
export const adminListConversations = (params = {}) =>
  api.get("/modul-d/admin/conversations", { params }).then(r => r.data);
export const adminGetConversation = (id) =>
  api.get(`/modul-d/admin/conversations/${id}`).then(r => r.data);
export const adminEscalate = (id) =>
  api.post(`/modul-d/admin/conversations/${id}/escalate`).then(r => r.data);
export const adminDeescalate = (id) =>
  api.post(`/modul-d/admin/conversations/${id}/deescalate`).then(r => r.data);
export const adminCloseConversation = (id, reason = "MANUAL", system_message = null) =>
  api.post(`/modul-d/admin/conversations/${id}/close`, { reason, system_message }).then(r => r.data);
export const adminAnalyticsBlocks = (params = {}) =>
  api.get("/modul-d/admin/analytics/blocks", { params }).then(r => r.data);
export const adminAnalyticsEvents = (params = {}) =>
  api.get("/modul-d/admin/analytics/events", { params }).then(r => r.data);
export const adminSchedulerTick = () =>
  api.post("/modul-d/admin/scheduler/tick").then(r => r.data);
export const adminUpdateFeatureFlags = (body) =>
  api.put("/modul-d/admin/feature-flags", body).then(r => r.data);

export const adminGetFeatureFlags = () =>
  api.get("/modul-d/feature-flags").then(r => r.data);

// ─── Message Reports (Sub-Batch 4c.5) ─────────────────────────────────
export const reportMessage = (messageKind, messageId, reasonCode, comment = "") =>
  api.post(`/modul-d/messages/${messageKind}/${messageId}/report`, {
    reason_code: reasonCode,
    comment: comment || null,
  }).then(r => r.data);

export const REPORT_REASON_CODES = [
  { code: "HARASSMENT_THREAT", label: "Belästigung / Drohung" },
  { code: "SEXUAL_INAPPROPRIATE", label: "Sexuell unangemessen / illegal" },
  { code: "CHILD_SAFETY", label: "Minderjährigenschutz" },
  { code: "CIRCUMVENTION_ATTEMPT", label: "Plattform-Umgehung" },
  { code: "CONTACT_DATA", label: "Kontaktdaten-Weitergabe" },
  { code: "SPAM", label: "Spam / Werbung" },
  { code: "OTHER", label: "Sonstiges" },
];

export const REASON_CODE_LABELS = {
  CONTACT_DATA: "Kontaktdaten erkannt",
  SEXUAL_CONTENT: "Unzulässige Inhalte",
  CIRCUMVENTION: "Plattform-Umgehung",
  MULTIPLE: "Mehrere Regelverstöße",
  ACCOUNT_SUSPENDED: "Konto gesperrt",
};

export const adminListReports = (params = {}) =>
  api.get("/modul-d/admin/reports", { params }).then(r => r.data);

export const adminGetReport = (reportId) =>
  api.get(`/modul-d/admin/reports/${reportId}`).then(r => r.data);

export const adminReviewReport = (reportId, body) =>
  api.post(`/modul-d/admin/reports/${reportId}/review`, body).then(r => r.data);

// ─── Helpers ────────────────────────────────────────────────────────
/**
 * Extracts a friendly error object from a Modul D error response.
 * Recognizes:
 *  - 422 CONTENT_BLOCKED  → { blocked, reason_code, reason_text }
 *  - 422 FEATURE_DISABLED → { feature_disabled, area }
 *  - 403 ACCOUNT_SUSPENDED → { suspended, suspended_until, suspension_reason, message }
 *  - sonst                → { blocked: false, message }
 */
export function parseInquiryError(err) {
  const detail = err?.response?.data?.detail;
  if (detail && typeof detail === "object" && detail.code === "CONTENT_BLOCKED") {
    return {
      blocked: true,
      reason_code: detail.primary_reason_code,
      reason_text: detail.primary_reason_text,
      reason_codes: detail.reason_codes || [],
    };
  }
  if (detail && typeof detail === "object" && detail.code === "FEATURE_DISABLED") {
    return { feature_disabled: true, area: detail.area };
  }
  if (detail && typeof detail === "object" && detail.code === "ACCOUNT_SUSPENDED") {
    let untilStr = "";
    if (detail.suspended_until) {
      try {
        const d = new Date(detail.suspended_until);
        untilStr = d.toLocaleString("de-DE", { dateStyle: "long", timeStyle: "short" });
      } catch { untilStr = String(detail.suspended_until); }
    }
    const baseMsg =
      "Dein Konto ist derzeit gesperrt. Senden ist während der Sperrzeit nicht möglich.";
    const reason = detail.suspension_reason ? ` Grund: ${detail.suspension_reason}.` : "";
    const until = untilStr ? ` Die Sperre endet am ${untilStr}.` : "";
    return {
      suspended: true,
      blocked: true, // also flag as blocked so existing block-banner code paths display it
      reason_code: "ACCOUNT_SUSPENDED",
      reason_text: baseMsg + reason + until,
      suspended_until: detail.suspended_until,
      suspension_reason: detail.suspension_reason,
      message: baseMsg + reason + until,
    };
  }
  return {
    blocked: false,
    message: typeof detail === "string" ? detail : (err?.message || "Fehler beim Senden"),
  };
}
